import {
    ApolloError,
    FetchResult,
    gql,
    MutationTuple,
    useMutation,
    QueryResult,
    useApolloClient,
    useQuery,
    StoreObject,
} from '@apollo/client'
import {useAlert} from 'react-alert'
import {DataEditFieldUpsert} from '~/globalTypes'
import {getGraphQLErrorMessage} from '~/util'
import {useQueryAllHomeItemData} from './AllHomeItemData'
import {useQueryDataEditGetValues} from './DataEditValue'
import {useQueryListCategories} from './ListCategories'
import {
    DataEditUpdateFields,
    DataEditUpdateFieldsVariables,
    DataEditUpdateFields_updateDataEditFields,
} from './__types__/DataEditUpdateFields'
import {useEffect, useState} from 'react'
import {
    DataEditGetFields,
    DataEditGetFieldsVariables,
    DataEditGetFields_dataEditFields,
} from './__types__/DataEditGetFields'
import {DataEditGetValues_dataEditValues} from './__types__/DataEditGetValues'
import {AllHomeItemQuery_organisation} from './__types__/AllHomeItemQuery'
import {ListCategories_organisation} from './__types__/ListCategories'

const DATA_EDIT_GET_FIELDS = gql`
    query DataEditGetFields($organisationIdStr: String!, $fieldName: String) {
        dataEditFields(organisationIdStr: $organisationIdStr, fieldName: $fieldName) {
            idStr
            organisationIdStr
            type
            subType
            name
            availableValues
            availableValueIdStrs
        }
    }
`

const DATA_EDIT_UPDATE_FIELDS = gql`
    mutation DataEditUpdateFields(
        $organisationIdStr: String!
        $dataEditFields: [DataEditFieldUpsert!]!
        $removeMissingFields: Boolean!
    ) {
        updateDataEditFields(
            organisationIdStr: $organisationIdStr
            dataEditFields: $dataEditFields
            removeMissingFields: $removeMissingFields
        ) {
            idStr
            organisationIdStr
            type
            subType
            name
            availableValues
            availableValueIdStrs
        }
    }
`

export const useQueryDataEditGetFields = (
    organisationIdStr: string,
    noCache?: boolean,
): [QueryResult<DataEditGetFields, DataEditGetFieldsVariables>, DataEditGetFields_dataEditFields[]] => {
    const query = useQuery<DataEditGetFields, DataEditGetFieldsVariables>(DATA_EDIT_GET_FIELDS, {
        variables: {organisationIdStr},
        ...(noCache ? {fetchPolicy: 'no-cache'} : {}),
    })
    const [dataView, setDataView] = useState<DataEditGetFields_dataEditFields[]>([])

    useEffect(() => {
        setDataView(query.data?.dataEditFields || [])
    }, [query])

    return [query, dataView]
}

export const cachePolicyDataEditGetFields = {
    read: (existing: StoreObject[] = []) => existing,
    merge: (existing: StoreObject[] = [], incoming: StoreObject[]) => incoming,
}

export const useCacheDataEditGetFields = () => {
    const client = useApolloClient()
    const read = (organisationIdStr: string) =>
        client.readQuery<DataEditGetFields, DataEditGetFieldsVariables>({
            query: DATA_EDIT_GET_FIELDS,
            variables: {organisationIdStr},
        })?.dataEditFields || []

    const write = (
        organisationIdStr: string,
        values:
            | DataEditGetFields_dataEditFields[]
            | ((currentData: DataEditGetFields_dataEditFields[]) => DataEditGetFields_dataEditFields[]),
    ) => {
        const data = (typeof values === 'function' ? values(read(organisationIdStr)) : values).map((newValue) => ({
            ...newValue,
            id: `DataEditField:{"idStr":${newValue.idStr}}`,
        }))
        client.writeQuery({
            query: DATA_EDIT_GET_FIELDS,
            data: {
                dataEditFields: data,
            },
            variables: {organisationIdStr},
        })
    }

    return [
        {
            read,
            write,
        },
    ]
}

export const useMutationDataEditUpdateFields = (
    organisationIdStr: string,
): [
    MutationTuple<DataEditUpdateFields, DataEditUpdateFieldsVariables>,
    (variables: {
        dataEditFields: DataEditFieldUpsert[]
        removeMissingFields: boolean
    }) => Promise<{
        mutationResult: FetchResult<DataEditUpdateFields, Record<string, any>, Record<string, any>>
        fields: DataEditUpdateFields_updateDataEditFields[]
        values: DataEditGetValues_dataEditValues[]
        homeItemsOrganisation: AllHomeItemQuery_organisation
        listCategoriesOrganisation: ListCategories_organisation
    }>,
] => {
    const [dataEditFieldsQuery] = useQueryDataEditGetFields(organisationIdStr)
    const [dataEditFieldsCache] = useCacheDataEditGetFields()
    const [dataEditValuesQuery] = useQueryDataEditGetValues(organisationIdStr)
    const [allHomeItemQuery] = useQueryAllHomeItemData(organisationIdStr)
    const [listCategories] = useQueryListCategories(organisationIdStr)
    const alert = useAlert()
    const mutation = useMutation<DataEditUpdateFields, DataEditUpdateFieldsVariables>(DATA_EDIT_UPDATE_FIELDS, {})
    const callMutation = async (variables: {dataEditFields: DataEditFieldUpsert[]; removeMissingFields: boolean}) => {
        try {
            const result = await mutation[0]({variables: {organisationIdStr, ...variables}})
            if (variables.removeMissingFields) {
                const responses = await Promise.all([
                    dataEditFieldsCache.write(organisationIdStr, result.data!.updateDataEditFields),
                    dataEditValuesQuery.refetch(),
                    allHomeItemQuery.refetch(),
                    listCategories.refetch(),
                ])
                return {
                    mutationResult: result,
                    fields: result.data!.updateDataEditFields,
                    values: responses[1].data.dataEditValues,
                    homeItemsOrganisation: responses[2].data.organisation,
                    listCategoriesOrganisation: responses[3].data.organisation,
                }
            } else {
                const responses = await Promise.all([
                    dataEditFieldsQuery.refetch(),
                    dataEditValuesQuery.refetch(),
                    allHomeItemQuery.refetch(),
                    listCategories.refetch(),
                ])
                return {
                    mutationResult: result,
                    fields: responses[0].data.dataEditFields,
                    values: responses[1].data.dataEditValues,
                    homeItemsOrganisation: responses[2].data.organisation,
                    listCategoriesOrganisation: responses[3].data.organisation,
                }
            }
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}
